import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewContainerRef,
} from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { ActivatedRoute } from "@angular/router";
import moment from "moment";
import { firstValueFrom, Subscription } from "rxjs";
import { take } from "rxjs/operators";
import { ToolbarOptionsHelper } from "src/app/inspection/details/toolbar-options-helper";
import { UserRolePermissionsService } from "src/app/shared/services/user-role-permissions/user-role-permissions.service";

import {
  GENERAL_INFO_UPDATED_KO,
  GENERAL_INFO_UPDATED_OK,
} from "../../constants/constants";
import { GeneralInfo } from "../../model/general-info";
import {
  AdditionalFieldListItem,
  GeneralInfoListItem,
} from "../../model/general-Info-list-item";
import { GeneralInfoCommand } from "../../services/commands/general-info.command";
import { GeneralInfoService } from "../../services/general-info.service";
import { InspectionDetailsService } from "../../services/inspection-details.service";
import { PermissionsService } from "../../services/permissions.service";
import { GeneralInfoQuery } from "../../services/queries/general-info.query";

@Component({
  selector: "im-inspection-details-info",
  templateUrl: "./inspection-details-info.component.html",
  styleUrls: ["./inspection-details-info.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class InspectionDetailsInfoComponent implements OnInit, OnDestroy {
  public isLoading: boolean = true;
  public inspectionId: string;
  public generalInfo: GeneralInfo = null;
  public staticGeneralInfoItems: GeneralInfoListItem[] = [];
  public scopeOfWork: GeneralInfoListItem[] = [];
  public dynamicGeneralInfoItems: AdditionalFieldListItem[] = [];
  public isEditing = false;
  public allowEditInfo = false;

  private generalInfoSubscription: Subscription;

  public actions: any[] = [];

  public constructor(
    public generalInfoService: GeneralInfoService,
    public viewContainerRef: ViewContainerRef,
    public dialog: MatDialog,
    private route: ActivatedRoute,
    private permissionsService: PermissionsService,
    private userRolePermissionsService: UserRolePermissionsService,
    private snackBar: MatSnackBar,
    private toolbarOptionsHelper: ToolbarOptionsHelper,
    private inspectionDetailsService: InspectionDetailsService,
    private changeDetectorRef: ChangeDetectorRef,
  ) {}

  public async ngOnInit() {
    const permissions = await this.permissionsService.getPermissions();
    this.inspectionId = this.getInspectionIdFromUrl();
    this.allowEditInfo =
      (permissions.setInspectionsListConfig().allowNewInspection &&
        !this.userRolePermissionsService.isInspector() &&
        !this.userRolePermissionsService.isServiceProviderInspector()) ||
      this.userRolePermissionsService.isServiceProviderCoordinator();
    await this.getGeneralInfo();
    this.subscribeToGeneralInfoEvents();
  }

  public ngOnDestroy() {
    this.generalInfoSubscription?.unsubscribe();
  }

  public enableEditMode() {
    this.isEditing = true;
  }

  public onCancelEdition() {
    this.isEditing = false;
  }

  public async onGeneralInfoSaved(generalInfo: GeneralInfo) {
    try {
      this.isLoading = true;
      this.generalInfoService.invokeCommand(
        new GeneralInfoCommand(this.inspectionId, generalInfo),
      );
      generalInfo.externalCoordinators =
        await this.inspectionDetailsService.getAssignedServiceProviderCoordinators(
          this.inspectionId,
        );
      this.setGeneralInfoData(generalInfo);
      this.toolbarOptionsHelper.setTitle(generalInfo.externalId);
    } catch (err) {
      this.isEditing = false;
      this.isLoading = false;
      throw err;
    }
  }

  public formatText(text: string): string {
    return text.replace(/\n/g, "<br>");
  }

  private subscribeToGeneralInfoEvents(): void {
    this.generalInfoSubscription =
      this.generalInfoService.generalInfoSent.subscribe((success: boolean) => {
        const text = success
          ? GENERAL_INFO_UPDATED_OK
          : GENERAL_INFO_UPDATED_KO;
        this.isEditing = false;
        this.isLoading = false;
        this.changeDetectorRef.markForCheck();
        this.snackBar.open(text, "OK", {
          duration: 3000,
          data: { color: "primary" },
        });
      });
  }

  private async getGeneralInfo(): Promise<void> {
    try {
      const generalInfoPromise = firstValueFrom(
        this.generalInfoService.generalInfo.pipe(take(1)),
      );
      await this.generalInfoService.invokeQuery(
        new GeneralInfoQuery(this.inspectionId),
      );
      this.setGeneralInfoData(await generalInfoPromise);
    } finally {
      this.isLoading = false;
      this.changeDetectorRef.detectChanges();
    }
  }

  private getDynamicGeneralInfoList(generalInfo: GeneralInfo) {
    const dynamicGeneralInfoItemsList: AdditionalFieldListItem[] = [];
    generalInfo.additionalFields.forEach((additionalField) => {
      dynamicGeneralInfoItemsList.push({
        id: additionalField.id,
        icon: "",
        name: additionalField.name,
        value: additionalField.value,
        controlType: additionalField.controlType,
        mandatory: additionalField.mandatory,
      });
    });
    return dynamicGeneralInfoItemsList;
  }

  private getStaticGeneralInfoList(
    generalInfo: GeneralInfo,
  ): GeneralInfoListItem[] {
    const generalInfoList = [
      {
        id: "1",
        icon: "assignment_turned_in_24px",
        title: "Order ID",
        lines: [generalInfo.externalId],
      },
      {
        id: "3",
        icon: "person_pin_24px",
        title: "Client",
        lines: [generalInfo.client],
      },
      {
        id: "4",
        icon: "room_24px",
        title: "Inspection Address ",
        lines: [generalInfo.address?.text],
      },
      {
        id: "5",
        icon: "insert_invitation_24px",
        title: "Dates",
        lines: generalInfo.date.map((date) => this.formatDate(date)),
      },
      {
        id: "6",
        icon: "chrome_reader_mode",
        title: "Inspection Reference",
        lines: [generalInfo.inspectionReference],
      },
    ];
    if (generalInfo?.externalCoordinators?.length) {
      let formExternalCoordinator = "";
      generalInfo.externalCoordinators.forEach((c) => {
        if (formExternalCoordinator) formExternalCoordinator += `\n` + c;
        else formExternalCoordinator = c;
      });
      if (this.userRolePermissionsService.isServiceProvider()) {
        generalInfoList.push({
          id: "7",
          icon: "group_add",
          title: "External Coordinator Assigned",
          lines: [formExternalCoordinator],
        });
      } else {
        generalInfoList.push({
          id: "2",
          icon: "laptop_windows_24px",
          title: "Origin Application",
          lines: [generalInfo.originApplicationName],
        });
        generalInfoList.push({
          id: "7",
          icon: "group_add",
          title: "External Coordinator Assigned",
          lines: [formExternalCoordinator],
        });
      }
    } else {
      generalInfoList.push({
        id: "2",
        icon: "laptop_windows_24px",
        title: "Origin Application",
        lines: [generalInfo.originApplicationName],
      });
    }

    generalInfoList.sort((a, b) => {
      if (a.id > b.id) return 1;
      else return -1;
    });

    return generalInfoList;
  }

  private getScopeOfWork(generalInfo: GeneralInfo): GeneralInfoListItem[] {
    return [
      {
        id: "8",
        icon: "list_24px",
        title: "Scope of Work",
        lines: [generalInfo.scopeOfWork],
      },
    ];
  }

  private formatDate(date: Date): string {
    return (
      new Date(date).toLocaleString([], {
        year: "numeric",
        month: "numeric",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }) + moment(date).format(" (UTCZ)")
    );
  }

  private setGeneralInfoData(generalInfo: GeneralInfo): void {
    this.generalInfo = { ...generalInfo };
    this.scopeOfWork = this.getScopeOfWork(generalInfo);
    this.staticGeneralInfoItems = this.getStaticGeneralInfoList(generalInfo);
    this.dynamicGeneralInfoItems = this.getDynamicGeneralInfoList(generalInfo);
  }

  private getInspectionIdFromUrl(): string {
    return (
      this.route.snapshot.params?.id ??
      this.route.snapshot.parent.paramMap.get("id")
    );
  }
}
